import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Upload, X as XIcon, FileText } from 'lucide-react';

import api from '../../services/api';
import { getAtsSettings, updateAtsSettings } from '../../services/atsSettingsService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import Tabs from '../../components/ui/Tabs';
import TextInput from '../../components/ui/TextInput.jsx';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import DocumentViewerModal from '../../components/ui/DocumentViewerModal';
import PipelineStagesTab from './PipelineStagesTab';
// Microsoft 365 takvim senkronu (tek yön) — mülakat planlandığında iç
// katılımcıların takvimine yazar. Buraya konur çünkü bağlantının TEK
// tüketicisi mülakat takvimidir ve ekranın yetkisi zaten AT'dir.
import CalendarIntegrationTab from './CalendarIntegrationTab';
// Aday etiketi kataloğu (SPEC F1) — adaylara atama aday detay sayfasındadır.
import CandidateTagsTab from './CandidateTagsTab';
// PARTİ 19 — Bildirim Şablonları ekranı ATS'e de açıldı. Bileşen PM'in
// klasöründe DURUYOR (tek kaynak; ikinci bir kopya iki ekranı ayrıştırırdı);
// listeyi hangi olayların oluşturduğunu SUNUCU belirler: kullanıcı yalnız
// `Write` yetkisi olan modüllerin olaylarını görür
// (NotificationTemplateController.AllowedModuleCodes). Yani AT.Write'lı ama
// PM'siz bir İK uzmanı burada YALNIZ `ats.*` metinlerini görür.
// Neden buraya: /performance/settings rotası `privileges={['PM.Write']}
// moduleGated module="PM"` taşıyor — PM'i olmayan kiracı o sayfaya HİÇ
// giremiyordu, dolayısıyla satın aldığı ATS'in bildirim metnini
// değiştiremiyordu (çıkmaz).
import NotificationTemplatesTab from '../performance-settings/NotificationTemplatesTab';
import {
  RECRUITING_COST_CURRENCIES, DEFAULT_RECRUITING_COST_CURRENCY,
} from '../ats-requisitions/recruitingCost';

const Wrapper = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  max-width: 720px;
  margin-top: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px;
`;

const Description = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: #6B7280;
  margin: 0 0 20px;
`;

const Hint = styled.p`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #6B7280;
  margin: 8px 0 0;
`;

const InlineActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 10px;
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.primary.main};
  text-decoration: underline;
  cursor: pointer;
  font-family: ${({ theme }) => theme.metrics.font.family};
`;

const FileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1.5px dashed #D5DCE3;
  border-radius: 8px;
  padding: 12px 14px;
  background: #FAFBFC;
  margin-top: 12px;
`;

const FileLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.dark.little};
  font-size: 13px;
  flex: 1;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  padding: 4px;
  &:hover { color: ${({ theme }) => theme.colors.auxiliar.danger}; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const KVKK_SECTIONS = ['controller', 'data', 'purpose', 'legalBasis', 'transfer', 'retention', 'rights', 'contact'];
const TEMPLATE_ACCEPT = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

// FE-10 — sayfa iki sekmeli: "Genel" (aydınlatma metni + teklif mektubu) ve
// "Süreç Aşamaları". İlan listesindeki "Aşamalar" aksiyonu buraya
// `?tab=stages&jobPostingId=<id>` ile derin bağlanır; o durumda aşama sekmesi
// ilana ÖZEL listeyi yönetir (ilan başlığı router state'inden gelir, yoksa
// kapsam adı boş kalır ama ekran çalışır).
export default function AtsSettingsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();
  const writable = canWrite('AT');
  const fileRef = useRef();
  const location = useLocation();

  const { deepLinkTab, postingId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return { deepLinkTab: params.get('tab'), postingId: params.get('jobPostingId') };
  }, [location.search]);
  const postingTitle = location.state?.postingTitle || '';

  const [activeTab, setActiveTab] = useState(() => {
    if (deepLinkTab === 'stages' || postingId) return 'stages';
    if (deepLinkTab === 'notifications') return 'notifications';
    if (deepLinkTab === 'calendar') return 'calendar';
    if (deepLinkTab === 'candidateTags') return 'candidateTags';
    return 'general';
  });
  useEffect(() => {
    if (deepLinkTab === 'stages' || postingId) setActiveTab('stages');
    else if (deepLinkTab === 'notifications') setActiveTab('notifications');
    else if (deepLinkTab === 'calendar') setActiveTab('calendar');
    else if (deepLinkTab === 'candidateTags') setActiveTab('candidateTags');
  }, [deepLinkTab, postingId]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [consentText, setConsentText] = useState('');
  const [consentVersion, setConsentVersion] = useState(1);
  const [offerLetterMode, setOfferLetterMode] = useState('system');
  const [templateFileUrl, setTemplateFileUrl] = useState('');
  const [templateFileName, setTemplateFileName] = useState('');
  // Açık olduğunda DocumentViewerModal'ı besler. Şablon dosyası özel sınıfa taşındı
  // (StorageVisibility.Private — offerLetter amacı) — kayıtlı adres artık doğrudan bir <a href>
  // ile açılamaz. DocumentViewerModal kayıtlı adresi kendisi `/v1/storage/view` üzerinden çözer.
  const [templateViewing, setTemplateViewing] = useState(false);
  // PARTİ 16 — KVKK saklama politikası. Süre METİN olarak tutulur: sayı
  // state'inde kullanıcı alanı temizleyemez (0'a düşer) ve 0 = "hemen imha et"
  // anlamına gelirdi; boş dize kullanıcıya yazarken nefes aldırır, kaydederken
  // sayıya çevrilir ve aralık dışı değeri sunucu 400 ile reddeder.
  const [retentionMonths, setRetentionMonths] = useState(String(12));
  const [anonymizationEnabled, setAnonymizationEnabled] = useState(false);
  // SPEC F3 — işe alım maliyeti para birimi. Maliyet kaydı YALNIZ bu birimde
  // girilir; değişirse eski kayıtlar kendi birimiyle kalır ve raporlar onları
  // toplamaz (döviz çevrimi yok).
  const [recruitingCostCurrency, setRecruitingCostCurrency] = useState(DEFAULT_RECRUITING_COST_CURRENCY);

  useEffect(() => {
    setLoading(true);
    getAtsSettings()
      .then((res) => {
        const s = res.data?.data;
        if (!s) return;
        setConsentText(s.careerConsentText || '');
        setConsentVersion(s.careerConsentVersion || 1);
        setOfferLetterMode(s.offerLetterMode || 'system');
        setTemplateFileUrl(s.offerLetterTemplateFileUrl || '');
        setTemplateFileName(s.offerLetterTemplateFileName || '');
        setRetentionMonths(String(s.candidateRetentionMonths ?? 12));
        setAnonymizationEnabled(Boolean(s.candidateAnonymizationEnabled));
        setRecruitingCostCurrency(s.recruitingCostCurrency || DEFAULT_RECRUITING_COST_CURRENCY);
      })
      .finally(() => setLoading(false));
  }, []);

  // "Standart metni yükle": şirket, Flexbee'nin standart aydınlatma metnini
  // düzenlemeye başlangıç noktası olarak alabilsin (sıfırdan yazmak zorunda kalmasın).
  const loadStandardText = useCallback(() => {
    const company = t('career.kvkk.companyFallback');
    const parts = [t('career.kvkk.intro', { company })];
    KVKK_SECTIONS.forEach((s) => {
      parts.push(`${t(`career.kvkk.section.${s}.title`)}\n${t(`career.kvkk.section.${s}.body`, { company })}`);
    });
    setConsentText(parts.join('\n\n'));
  }, [t]);

  const handleTemplateFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const presignRes = await api.post(
        `/v1/storage/presign?fileName=${encodeURIComponent(file.name)}&mimeType=${encodeURIComponent(file.type || 'application/octet-stream')}&purpose=${encodeURIComponent('offerLetter')}`,
      );
      const { upload_url, file_url } = presignRes.data;
      await axios.put(upload_url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      setTemplateFileUrl(file_url);
      setTemplateFileName(file.name);
    } catch { /* interceptor */ }
    setUploading(false);
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await updateAtsSettings({
        careerConsentText: consentText.trim() || null,
        offerLetterMode,
        offerLetterTemplateFileUrl: templateFileUrl || null,
        offerLetterTemplateFileName: templateFileName || null,
        // Boş/geçersiz alan null gider = "bu politikayı değiştirme"; sunucu
        // mevcut değeri korur. Aksi hâlde alanı silen kullanıcı saklama
        // süresini sessizce 0'a çekerdi.
        candidateRetentionMonths: Number.isFinite(parseInt(retentionMonths, 10))
          ? parseInt(retentionMonths, 10)
          : null,
        candidateAnonymizationEnabled: anonymizationEnabled,
        recruitingCostCurrency,
      });
      const s = res.data?.data;
      if (s) {
        setConsentText(s.careerConsentText || '');
        setConsentVersion(s.careerConsentVersion || 1);
        setRetentionMonths(String(s.candidateRetentionMonths ?? 12));
        setAnonymizationEnabled(Boolean(s.candidateAnonymizationEnabled));
        setRecruitingCostCurrency(s.recruitingCostCurrency || DEFAULT_RECRUITING_COST_CURRENCY);
      }
    } catch { /* interceptor */ }
    setSaving(false);
  }, [consentText, offerLetterMode, templateFileUrl, templateFileName, retentionMonths, anonymizationEnabled,
    recruitingCostCurrency]);

  const modeOptions = [
    { id: 'system', name: t('ats.settings.offerLetterMode.system') },
    { id: 'custom', name: t('ats.settings.offerLetterMode.custom') },
  ];

  const tabs = [
    { key: 'general', label: t('ats.settings.tab.general') },
    { key: 'stages', label: t('ats.settings.tab.stages') },
    { key: 'notifications', label: t('ats.settings.tab.notifications') },
    { key: 'calendar', label: t('ats.settings.tab.calendar') },
    { key: 'candidateTags', label: t('ats.settings.tab.candidateTags') },
  ];

  return (
    <Wrapper>
      <Breadcrumbs items={[{ label: t('ats.settings.title') }]} />

      <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />

      {activeTab === 'stages' && (
        <PipelineStagesTab jobPostingId={postingId || null} postingTitle={postingTitle} />
      )}

      {activeTab === 'notifications' && <NotificationTemplatesTab />}

      {activeTab === 'calendar' && <CalendarIntegrationTab />}

      {activeTab === 'candidateTags' && <CandidateTagsTab />}

      {activeTab === 'general' && !loading && (
      <>
      <Card>
        <Title>{t('ats.settings.consentTitle')}</Title>
        <Description>{t('ats.settings.consentDescription')}</Description>
        <TextInput
          label={t('ats.settings.consentText')}
          type="textarea"
          rows={12}
          value={consentText}
          onChange={(e) => setConsentText(e.target.value)}
          disabled={!writable}
          placeholder={t('ats.settings.consentPlaceholder')}
        />
        <Hint>{t('ats.settings.consentHint', { version: consentVersion })}</Hint>
        {writable && (
          <InlineActions>
            <LinkBtn type="button" onClick={loadStandardText}>
              {t('ats.settings.loadStandardText')}
            </LinkBtn>
            {consentText && (
              <LinkBtn type="button" onClick={() => setConsentText('')}>
                {t('ats.settings.clearCustomText')}
              </LinkBtn>
            )}
          </InlineActions>
        )}
      </Card>

      <Card>
        <Title>{t('ats.settings.offerLetterTitle')}</Title>
        <Description>{t('ats.settings.offerLetterDescription')}</Description>
        {writable ? (
          <GenericSelect
            label={t('ats.settings.offerLetterModeLabel')}
            options={modeOptions}
            value={offerLetterMode}
            onChange={(v) => setOfferLetterMode(v || 'system')}
            idKey="id"
            labelKey="name"
          />
        ) : (
          <TextInput
            label={t('ats.settings.offerLetterModeLabel')}
            value={t(`ats.settings.offerLetterMode.${offerLetterMode}`)}
            disabled
          />
        )}
        <Hint>
          {offerLetterMode === 'custom'
            ? t('ats.settings.offerLetterModeCustomHint')
            : t('ats.settings.offerLetterModeSystemHint')}
        </Hint>

        {offerLetterMode === 'custom' && (
          <FileRow>
            {writable && (
              <Button type="button" disabled={uploading} onClick={() => fileRef.current?.click()}>
                <Upload size={14} />
                {uploading ? '...' : t('ats.settings.uploadTemplate')}
              </Button>
            )}
            <FileLabel>
              {templateFileUrl ? (
                <>
                  <FileText size={14} />
                  <LinkBtn
                    type="button"
                    data-testid="offer-letter-template-view-trigger"
                    onClick={() => setTemplateViewing(true)}
                  >
                    {templateFileName || t('ats.settings.templateFile')}
                  </LinkBtn>
                </>
              ) : (
                t('ats.settings.templateHint')
              )}
            </FileLabel>
            {templateFileUrl && writable && (
              <RemoveBtn
                type="button"
                onClick={() => { setTemplateFileUrl(''); setTemplateFileName(''); }}
                aria-label={t('common.delete')}
              >
                <XIcon size={16} />
              </RemoveBtn>
            )}
            <input
              ref={fileRef}
              type="file"
              accept={TEMPLATE_ACCEPT}
              onChange={handleTemplateFile}
              style={{ display: 'none' }}
            />
          </FileRow>
        )}
      </Card>

      <Card>
        <Title>{t('ats.settings.retentionTitle')}</Title>
        <Description>{t('ats.settings.retentionDescription')}</Description>
        <TextInput
          label={t('ats.settings.retentionMonthsLabel')}
          type="number"
          min={1}
          max={120}
          value={retentionMonths}
          onChange={(e) => setRetentionMonths(e.target.value)}
          disabled={!writable}
        />
        <Hint>{t('ats.settings.retentionMonthsHint')}</Hint>

        {writable ? (
          <div style={{ marginTop: 16 }}>
            <CheckboxInput
              label={t('ats.settings.anonymizationLabel')}
              checked={anonymizationEnabled}
              onChange={setAnonymizationEnabled}
            />
          </div>
        ) : (
          <TextInput
            label={t('ats.settings.anonymizationLabel')}
            value={anonymizationEnabled ? t('ats.settings.anonymizationOn') : t('ats.settings.anonymizationOff')}
            disabled
          />
        )}
        <Hint data-testid="ats-anonymization-hint">
          {anonymizationEnabled
            ? t('ats.settings.anonymizationOnHint')
            : t('ats.settings.anonymizationOffHint')}
        </Hint>
        <Hint>{t('ats.settings.anonymizationProtected')}</Hint>
      </Card>

      <Card data-testid="ats-recruiting-cost-currency-card">
        <Title>{t('ats.settings.recruitingCostTitle')}</Title>
        <Description>{t('ats.settings.recruitingCostDescription')}</Description>
        {writable ? (
          <GenericSelect
            label={t('ats.settings.recruitingCostCurrencyLabel')}
            options={RECRUITING_COST_CURRENCIES.map((c) => ({ id: c, name: c }))}
            value={recruitingCostCurrency}
            onChange={(v) => setRecruitingCostCurrency(v || DEFAULT_RECRUITING_COST_CURRENCY)}
            idKey="id"
            labelKey="name"
            clearable={false}
          />
        ) : (
          <TextInput
            label={t('ats.settings.recruitingCostCurrencyLabel')}
            value={recruitingCostCurrency}
            disabled
          />
        )}
        <Hint>{t('ats.settings.recruitingCostCurrencyHint')}</Hint>
      </Card>

      {writable && (
        <Actions>
          <Button onClick={handleSave} disabled={saving || uploading}>
            {saving ? '...' : t('common.save')}
          </Button>
        </Actions>
      )}

      <DocumentViewerModal
        open={templateViewing}
        fileUrl={templateFileUrl}
        title={templateFileName}
        onClose={() => setTemplateViewing(false)}
      />
      </>
      )}
    </Wrapper>
  );
}
